<template>
  <div class="group-container" style="overflow: hidden; height: 100%">
    <div class="group-title-container">
      <div>Merge {{ groupName }}</div>
      <div>
        <HelpIcon
          text="We force name as the first column of this table.
            Other columns reflects your list page column settings. To see a
            different set of columns, please adapt your settings and re-enter
            the modal"
        />
      </div>
    </div>
    <div class="sample-table-container">
      <table class="sample-table">
        <thead>
          <tr>
            <th
              v-for="(column, index) in displayedColumns"
              :key="`colhead-${column.id}${index}`"
              class="head-cell"
              :class="{
                'fixed-left': column.id === 'name',
              }"
            >
              <div class="is-flex is-justify-content-space-between">
                <div class="is-flex">
                  <span
                    v-if="column.category === 'annotation'"
                    v-tooltip="'This is an annotation'"
                    class="icon"
                  >
                    <i class="fa-solid fa-xs fa-tags" />
                  </span>
                  <span v-tooltip="column.label" class="th__label field-wrap">
                    {{ column.label }}
                  </span>
                </div>
                <div
                  v-if="column.id !== 'name'"
                  class="drop-col-icon has-text-grey-light"
                >
                  <span class="icon is-small" @click="dropColumn(column.id)">
                    <i class="fa-solid fa-times" />
                  </span>
                </div>
              </div>
            </th>
            <th
              class="head-cell fixed-right"
              :style="{ right: `${mostLeftOffset}px` }"
            >
              <span v-tooltip="'Change permission'" class="icon is-small">
                <i class="fa-solid fa-edit fa-sm" />
              </span>
            </th>
            <th
              ref="headSecondRight"
              v-tooltip="'Delete permission'"
              class="head-cell fixed-right"
              :style="{ right: `${rightOffset}px` }"
            >
              <span class="icon is-small">
                <i class="fa-solid fa-trash fa-sm" />
              </span>
            </th>
            <th
              ref="headFixedRight"
              class="head-cell fixed-right"
              style="right: 0"
            >
              Keep
            </th>
          </tr>
        </thead>
        <tbody v-if="samples && samples.length">
          <template
            v-for="(sample, sampIndex) in samples"
            :key="`row-${sample.id.value}${sampIndex}`"
          >
            <tr>
              <td
                v-for="(column, index) in displayedColumns"
                :key="`sort-${column.id}${index}`"
                :style="keptSampleCellColorStyle(column.id, sample.id.value)"
                :class="{
                  'fixed-left': column.id === 'name',
                }"
              >
                <FieldComponent
                  :field-meta="column.fieldMeta"
                  :data="sample[column.id]"
                  class="field-wrap"
                />
              </td>
              <td class="fixed-right" :style="{ right: `${mostLeftOffset}px` }">
                <span
                  v-if="permissionsById[sample.id.value].includes('change')"
                  class="icon is-small has-text-grey"
                >
                  <i class="fa-solid fa-check fa-xs" />
                </span>
                <span v-else></span>
              </td>
              <td class="fixed-right" :style="{ right: `${rightOffset}px` }">
                <span
                  v-if="permissionsById[sample.id.value].includes('delete')"
                  class="icon is-small has-text-grey"
                >
                  <i class="fa-solid fa-check fa-xs" />
                </span>
                <span v-else></span>
              </td>
              <td class="fixed-right" style="right: 0">
                <div class="is-flex is-justify-content-center">
                  <span
                    v-tooltip="
                      !permissionsById[sample.id.value].includes('change')
                        ? 'You need the change permission to be able to keep this sample'
                        : undefined
                    "
                    class="icon"
                    :style="{
                      cursor: permissionsById[sample.id.value].includes(
                        'change',
                      )
                        ? 'pointer'
                        : 'not-allowed',
                    }"
                    :class="{
                      'has-text-gray':
                        permissionsById[sample.id.value].includes('change'),
                      'has-text-primary': sample.id.value === keptSampleId,
                      'has-text-danger':
                        sample.id.value === keptSampleId &&
                        !hasNecessaryPermissions,
                    }"
                    @click="handleKeepSample(sample.id.value)"
                  >
                    <i
                      class="fa-solid"
                      :class="[
                        sample.id.value === keptSampleId
                          ? 'fa-toggle-on'
                          : 'fa-toggle-off',
                      ]"
                    />
                  </span>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div class="notification-container is-flex-direction-column">
      <div
        v-if="canChangeKeptSample === false"
        class="notification is-danger"
        style="padding: 0.1rem; margin-bottom: 0.1rem"
      >
        <span class="icon">
          <i class="fa-solid fa-exclamation-triangle" />
        </span>
        <span style="width: 100%">
          You do not have
          <u>change</u>
          permissions on the sample to keep
        </span>
      </div>
      <div
        v-if="canDeleteDiscardedSamples === false"
        class="notification is-danger"
        style="padding: 0.1rem; margin-bottom: 0.1rem"
      >
        <span class="icon">
          <i class="fa-solid fa-exclamation-triangle" />
        </span>
        <span style="width: 100%">
          You do not have
          <u>change</u>
          <span
            v-tooltip="
              `Change permission is needed on deleted samples because
              they see their relationships modified before being soft-deleted`
            "
          >
            <span class="icon has-text-white">
              <i class="fa-solid fa-question-circle fa-sm" />
            </span>
          </span>
          and
          <u>delete</u>
          permissions on all samples that have to be deleted during the merge
        </span>
      </div>
    </div>
    <div>
      <p v-if="!keptSampleId" class="is-danger help" style="text-align: right">
        Please select the sample to keep
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MergeSampleGroup',
  provide() {
    return { fieldPage: 'list', linkBehavior: 'blank' }
  },
  emits: ['keep-sample'],
  props: {
    groupName: {
      type: String,
      default: 'Group <unknown>',
    },
    samples: {
      type: Array,
      default: () => [],
    },
    permissionsById: {
      type: Object,
      default: () => {},
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      keptSampleId: '',
      droppedColumns: [],
      rightOffset: 0,
      mostLeftOffset: 0,
    }
  },
  computed: {
    displayedColumns() {
      return this.columns.filter(
        (c) => c.isVisible === true && !this.droppedColumns.includes(c.id),
      )
    },
    discardedSamples() {
      return this.keptSampleId
        ? this.samples.filter((s) => s.id?.value !== this.keptSampleId)
        : []
    },
    discardedSampleIds() {
      return this.keptSampleId
        ? this.discardedSamples.map((s) => s.id?.value)
        : []
    },
    discardedSamplesPermissions() {
      return this.keptSampleId
        ? this.discardedSampleIds.map(
            (sampleId) => this.permissionsById[sampleId],
          )
        : []
    },
    canChangeKeptSample() {
      return this.permissionsById[this.keptSampleId]?.includes('change')
    },
    canDeleteDiscardedSamples() {
      return (
        this.discardedSamplesPermissions.length &&
        this.discardedSamplesPermissions
          .map((p) => p.includes('change') && p.includes('delete'))
          .every((p) => p === true)
      )
    },
    hasNecessaryPermissions() {
      return this.canChangeKeptSample && this.canDeleteDiscardedSamples
    },
  },
  methods: {
    handleKeepSample(sampleId) {
      this.$emit('keep-sample', { keptId: '', discardedIds: [] })
      this.keptSampleId = sampleId === this.keptSampleId ? '' : sampleId
      if (this.keptSampleId && this.hasNecessaryPermissions) {
        this.$emit('keep-sample', {
          keptId: this.keptSampleId,
          discardedIds: this.discardedSampleIds,
        })
      }
    },
    dropColumn(id) {
      this.droppedColumns.push(id)
    },
    keptSampleCellColorStyle(colId, rowSampleId) {
      let opacity
      if (
        colId !== 'name' &&
        this.keptSampleId &&
        rowSampleId !== this.keptSampleId
      ) {
        opacity = 0.4
      }
      let backgroundColor
      if (this.keptSampleId && rowSampleId === this.keptSampleId) {
        backgroundColor = this.hasNecessaryPermissions ? 'success' : 'warning'
      }
      return {
        backgroundColor: backgroundColor
          ? `var(--${backgroundColor})`
          : undefined,
        opacity,
        zIndex: colId === 'name' ? 1 : undefined,
      }
    },
    setFixedColumnsOffset() {
      this.$nextTick(() => {
        // + 1 and + 0.5 avoid border jittering on scroll...
        this.rightOffset = this.$refs?.headFixedRight?.clientWidth + 0.5
        this.mostLeftOffset =
          this.$refs?.headSecondRight?.clientWidth + this.rightOffset + 0.5
      })
    },
  },
  mounted() {
    this.setFixedColumnsOffset()
  },
}
</script>

<style scoped>
.group-container {
  width: 100%;
}

.group-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bolder;
  padding: 0.8rem;
}

.sample-table-container {
  border: 1px solid black;
  overflow: auto;
  width: 100%;
}

table.sample-table {
  display: block;
  table-layout: fixed;
  border-collapse: separate;
  max-height: 400px;
  overflow: auto;
  width: 100%;
}

table.sample-table thead tr th {
  border-bottom: 1px solid black;
  background: white;
}

table.sample-table thead tr th,
table.sample-table tbody tr td {
  padding: 0.2rem 0.7rem;
}

table.sample-table tbody tr:nth-child(odd) td,
table.sample-table tbody tr:nth-child(odd) td.fixed-left {
  background-color: var(--bg-default);
}

table.sample-table tbody tr:nth-child(even) td,
table.sample-table tbody tr:nth-child(even) td.fixed-left {
  background-color: var(--bg-muted);
}

th.fixed-left,
td.fixed-left {
  position: sticky;
  left: 0;
  border-right: 1px solid black;
  box-sizing: border-box;
}

th.fixed-right,
td.fixed-right {
  position: sticky;
  border-left: 1px solid black;
}

.field-wrap {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 1.5em;
}

.head-cell .drop-col-icon {
  padding-left: 0.5rem;
  cursor: pointer;
}

.head-cell:hover .drop-col-icon {
  color: var(--danger) !important;
}
</style>
